import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, ScrollView, View } from "react-native";
import Animated, { LinearTransition } from "react-native-reanimated";
import { SafeAreaView } from "react-native-safe-area-context";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { LeaveRequestListItem } from "@/features/submissions/LeaveRequestListItem";
import { isActiveLeaveRequest } from "@/features/home/leaveRequestStatus";
import { sortLeaveRequests } from "@/features/requests/sortLeaveRequests";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { formatIsoDate } from "@/lib/date";
import { useGetLeaveRequestsQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

const LAYOUT_TRANSITION = LinearTransition.duration(200);

type AbsencesScreenProps = {
    childId: number;
};

export function AbsencesScreen({ childId }: AbsencesScreenProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const todayIso = formatIsoDate(new Date());
    const {
        data: leaveRequests = [],
        isLoading: isLoadingRequests,
        refetch,
    } = useGetLeaveRequestsQuery(childId);

    const currentRequests = useMemo(
        () =>
            sortLeaveRequests(
                leaveRequests.filter((request) =>
                    isActiveLeaveRequest(request, todayIso),
                ),
            ),
        [leaveRequests, todayIso],
    );

    const onRefreshData = useCallback(() => refetch(), [refetch]);
    const { refreshControl } = usePullToRefresh(onRefreshData);

    function handleCreateLeaveRequest() {
        router.push({
            pathname: "/leave-request",
            params: { childId: String(childId) },
        });
    }

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="gap-8 px-4 pb-6 web:mx-auto web:w-full web:max-w-lg"
                refreshControl={refreshControl}
            >
                <View className="gap-1">
                    <Text variant="muted">{t("absences.subtitle")}</Text>
                </View>

                <View className="gap-2">
                    <Text
                        variant="caption"
                        className="px-1 uppercase tracking-wide"
                        testID={
                            TEST_IDS[TEST_IDS.ABSENCES_CURRENT_REQUESTS_SECTION]
                        }
                    >
                        {t("absences.currentTitle")}
                    </Text>

                    <View className="rounded-card border border-border bg-card px-4 shadow-none">
                        {isLoadingRequests ? (
                            <View className="items-center py-8">
                                <ActivityIndicator />
                            </View>
                        ) : currentRequests.length === 0 ? (
                            <View className="items-center gap-2 py-8">
                                <Text variant="label">
                                    {t("absences.currentEmptyTitle")}
                                </Text>
                                <Text variant="muted" className="text-center">
                                    {t("absences.currentEmptyDescription")}
                                </Text>
                            </View>
                        ) : (
                            currentRequests.map((request, index) => (
                                <LeaveRequestListItem
                                    key={request.id}
                                    request={request}
                                    isLast={
                                        index === currentRequests.length - 1
                                    }
                                />
                            ))
                        )}
                    </View>
                </View>
                <Animated.View layout={LAYOUT_TRANSITION}>
                    <Button
                        className="h-auto min-h-12 py-3"
                        onPress={handleCreateLeaveRequest}
                        testID={
                            TEST_IDS[
                                TEST_IDS.ABSENCES_CREATE_LEAVE_REQUEST_BUTTON
                            ]
                        }
                    >
                        <View className="flex-row items-center gap-2">
                            <Icon
                                name="plus"
                                iconStyle="solid"
                                className="text-primary-foreground"
                                size={17}
                            />
                            <Text className="text-primary-foreground">
                                {t("absences.newRequest")}
                            </Text>
                        </View>
                    </Button>
                </Animated.View>
            </ScrollView>
        </SafeAreaView>
    );
}
